// Equipment tab: roster, meter totals, maintenance status.
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState } from 'react';
import { useApi, putJson, delJson } from '../../api.js';

// ============================================================================
//  EquipmentPanel -- asset roster + accumulated hours + maintenance status.
// ============================================================================
// Two equipment categories: Vehicles are tracked in kilometers, plant
// Equipment is tracked in hours. The `source_column` on each row still
// picks a specific field on daily_ops_reports; the options offered
// depend on kind.
// Equipment types and the legacy daily-report column each can point at (hours for equipment, km for vehicles).
const EQUIPMENT_KINDS = [
  { value: 'equipment', label: 'Equipment (hours)', unit: 'hrs',
    sources: [
      { value: 'loader_hours',   label: 'Loader hours'   },
      { value: 'bobcat_hours',   label: 'Bobcat hours'   },
      { value: 'forklift_hours', label: 'Forklift hours' },
      { value: 'vac_hours',      label: 'Vac hours (legacy)' }
    ] },
  { value: 'vehicles',  label: 'Vehicles (km)',    unit: 'km',
    sources: [
      { value: 'vac_truck_km',     label: 'Vac truck km'    },
      { value: 'pickup_km',        label: 'Pickup km'       },
      { value: 'dump_truck_km',    label: 'Dump truck km'   },
      { value: 'other_vehicle_km', label: 'Other vehicle km'}
    ] }
];

function kindOf(k) { return EQUIPMENT_KINDS.find(x => x.value === k) || EQUIPMENT_KINDS[0]; }

function unitFor(eq) { return kindOf(eq.kind).unit; }

export default function EquipmentPanel({ canWrite = true }) {
  // Data: equipment list with computed totals and service status (/api/equipment).
  const { data, loading, error, refresh } = useApi('/api/equipment');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const equipment = (data && data.equipment) || [];
  const tableMissing = data && data.tableExists === false;

  const startNew = () => setEditing({
    // no source_column: new items store their own readings (2026-10-14 —
    // the old 'loader_hours' default made new items mirror the Loader)
    code: '', display_name: '', kind: 'equipment', source_column: null, metered: false,
    maint_interval_hours: 250, hours_at_last_service: 0, opening_balance: 0,
    last_service_date: '', last_service_notes: '', active: true, notes: ''
  });
  const startEdit = (e) => setEditing({ ...e });

  // Actions: save, delete, reset service, set total.
  async function save() {
    if (!editing) return;
    setSaving(true);
    try { await putJson('/api/equipment', editing); setEditing(null); refresh(); }
    catch (e) { alert('Save failed: ' + (e.message || e)); }
    finally { setSaving(false); }
  }
  async function remove(eq) {
    if (!confirm('Delete ' + eq.display_name + '?')) return;
    setSaving(true);
    try { await delJson('/api/equipment/' + eq.id); refresh(); }
    catch (e) { alert('Delete failed: ' + (e.message || e)); }
    finally { setSaving(false); }
  }
  // Reset the maintenance cycle: mark today as the last-service date and
  // pin hours_at_last_service to current total_hours so "hours since
  // service" starts at 0 and the OVERDUE badge clears. Preserves all
  // other fields.
  async function resetService(eq) {
    const today = new Date().toISOString().slice(0, 10);
    if (!confirm('Reset maintenance for ' + eq.display_name + '?\n\nSets last service date to ' + today + ' and clears the OVERDUE warning.')) return;
    setSaving(true);
    try {
      await putJson('/api/equipment', {
        ...eq,
        hours_at_last_service: eq.total_hours || 0,
        last_service_date: today
      });
      refresh();
    } catch (e) { alert('Reset failed: ' + (e.message || e)); }
    finally { setSaving(false); }
  }
  // Admin quick-edit: click the Total cell, enter the new meter reading,
  // and we back-solve opening_balance so the displayed total matches.
  // Formula: opening_balance = new_total - (currentTotal - currentOpening)
  //        = new_total - accumulated_daily_readings.
  async function setTotal(eq) {
    const u = unitFor(eq);
    const cur = eq.total_hours == null ? 0 : Number(eq.total_hours);
    const opening = eq.opening_balance == null ? 0 : Number(eq.opening_balance);
    const accumulated = cur - opening;  // daily readings sum
    const input = prompt(
      'Set Total for ' + eq.display_name + ' (' + u + ')\n\n' +
      'Current: ' + cur.toFixed(1) + ' ' + u + '\n' +
      'Daily accumulation so far: ' + accumulated.toFixed(1) + ' ' + u + '\n\n' +
      'Enter the new Total value (typically the current meter reading):',
      cur.toFixed(1)
    );
    if (input == null) return;
    const newTotal = Number(input);
    if (!Number.isFinite(newTotal)) { alert('Not a valid number.'); return; }
    const newOpening = +(newTotal - accumulated).toFixed(2);
    setSaving(true);
    try {
      await putJson('/api/equipment', { ...eq, opening_balance: newOpening });
      refresh();
    } catch (e) { alert('Update failed: ' + (e.message || e)); }
    finally { setSaving(false); }
  }

  if (loading && !data) return <div className="loading">Loading equipment...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  // Screen: toolbar, equipment table (totals + maintenance), add/edit pop-up.
  const fmtHours = h => h == null ? '' : Number(h).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <div>
      {tableMissing && (
        <div style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', color: '#7f1d1d' }}>
          <strong>equipment table missing.</strong> Run <code>db/schema-037-equipment.sql</code>.
        </div>
      )}
      {/* Toolbar: + Add equipment. */}
      <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
        {canWrite && (
          <button className="refresh-btn" onClick={startNew} style={{ background: '#0369a1' }}>+ Add equipment</button>
        )}
        <div className="small" style={{ color: '#64748b', alignSelf: 'center' }}>
          Totals accumulate automatically from Daily Ops Report entries (hours for equipment, km for vehicles).
        </div>
      </div>

      {/* Equipment table. Total = latest meter reading for metered items, otherwise opening balance + daily entries. Click Total to correct it. */}
      <table className="fs-table tidy" style={{ width: '100%', fontSize: '0.92em' }}>
        <thead>
          <tr>
            <th>Equipment</th>
            <th style={{ textAlign: 'right' }}>Total</th>
            <th style={{ textAlign: 'right' }}>Since last svc</th>
            <th style={{ textAlign: 'right' }}>Interval</th>
            <th style={{ textAlign: 'right' }}>Until svc</th>
            <th>Last svc date</th>
            {canWrite && <th style={{ width: 170 }}></th>}
          </tr>
        </thead>
        <tbody>
          {equipment.length === 0 && (
            <tr><td colSpan={canWrite ? 7 : 6} className="small" style={{ color: '#666', padding: '0.75rem' }}>
              No equipment yet.
            </td></tr>
          )}
          {equipment.map(eq => {
            const u = unitFor(eq);
            const withUnit = v => v == null ? '-' : `${fmtHours(v)} ${u}`;
            return (
            <tr key={eq.id} style={eq.active ? undefined : { color: '#94a3b8' }}>
              <td>
                <strong>{eq.display_name}</strong>
                <div className="small" style={{ color: '#64748b' }}>
                  {eq.code} &middot; {kindOf(eq.kind).label}
                  {eq.serial_number && <> &middot; S/N {eq.serial_number}</>}
                </div>
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {canWrite ? (
                  <button className="link" onClick={() => setTotal(eq)}
                          title="Click to set the current meter reading (adjusts opening balance)"
                          style={{ fontWeight: 600, color: '#0f172a', textDecoration: 'underline dotted' }}>
                    {withUnit(eq.total_hours)}
                  </button>
                ) : withUnit(eq.total_hours)}
              </td>
              <td style={{ textAlign: 'right' }}>{withUnit(eq.hours_since_service)}</td>
              <td style={{ textAlign: 'right' }}>{withUnit(eq.maint_interval_hours)}</td>
              <td style={{ textAlign: 'right',
                           color: eq.overdue ? '#b91c1c' : (eq.hours_until_service != null && eq.hours_until_service < 25 ? '#c2410c' : '#0f172a'),
                           fontWeight: eq.overdue ? 700 : 400 }}>
                {eq.hours_until_service == null ? '-'
                  : eq.overdue ? 'OVERDUE ' + fmtHours(-eq.hours_until_service) + ' ' + u
                  : withUnit(eq.hours_until_service)}
              </td>
              <td>{eq.last_service_date || <span className="small" style={{ color:'#94a3b8' }}>never</span>}</td>
              {canWrite && (
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="link" onClick={() => startEdit(eq)} style={{ marginRight: '0.4rem' }}>Edit</button>
                  <button className="link" onClick={() => resetService(eq)}
                          disabled={saving}
                          style={{ marginRight: '0.4rem', color: '#0369a1' }}
                          title="Mark serviced today and clear the OVERDUE badge">
                    Reset
                  </button>
                  <button className="link" onClick={() => remove(eq)} style={{ color: '#991b1b' }}>Delete</button>
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* Add / edit pop-up. "Has an hour meter" = operators enter the closing reading on the Daily Ops Report. */}
      {editing && (
        <div className="modal-backdrop" onClick={() => !saving && setEditing(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ minWidth: 320, maxWidth: 540 }}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit equipment' : 'Add equipment'}</h3>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              <label className="small">Code (unique, e.g. LOADER2)
                <input type="text" value={editing.code}
                  onChange={e => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                  style={{ width: '100%', textTransform: 'uppercase' }} />
              </label>
              <label className="small">Display name
                <input type="text" value={editing.display_name || ''}
                  onChange={e => setEditing({ ...editing, display_name: e.target.value })}
                  style={{ width: '100%' }} />
              </label>
              <label className="small">Type
                <select value={editing.kind || 'equipment'}
                  onChange={e => setEditing({ ...editing, kind: e.target.value })}
                  style={{ width: '100%' }}>
                  {EQUIPMENT_KINDS.map(k => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </label>
              <label className="small">Serial number
                <input type="text" value={editing.serial_number || ''}
                  onChange={e => setEditing({ ...editing, serial_number: e.target.value })}
                  placeholder="VIN, plate, or manufacturer serial"
                  style={{ width: '100%' }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <label className="small">Maintenance interval (hrs / km)
                  <input type="number" step="0.1" value={editing.maint_interval_hours == null ? '' : editing.maint_interval_hours}
                    onChange={e => setEditing({ ...editing, maint_interval_hours: e.target.value })}
                    style={{ width: '100%' }} />
                </label>
                <label className="small">Hours / km at last service
                  <input type="number" step="0.1" value={editing.hours_at_last_service == null ? 0 : editing.hours_at_last_service}
                    onChange={e => setEditing({ ...editing, hours_at_last_service: e.target.value })}
                    style={{ width: '100%' }} />
                </label>
              </div>
              <label className="small">Opening balance (meter reading before daily tracking started)
                <input type="number" step="0.1" value={editing.opening_balance == null ? 0 : editing.opening_balance}
                  onChange={e => setEditing({ ...editing, opening_balance: e.target.value })}
                  style={{ width: '100%' }} />
                <div className="small" style={{ color: '#64748b', marginTop: '0.15rem' }}>
                  Total = Opening + sum of daily readings. Use this to seed a pre-existing meter value. (Metered items show the latest meter reading instead.)
                </div>
              </label>
              <label className="small">Last service date
                <input type="date" value={editing.last_service_date || ''}
                  onChange={e => setEditing({ ...editing, last_service_date: e.target.value })}
                  style={{ width: '100%' }} />
              </label>
              <label className="small">Last service notes
                <input type="text" value={editing.last_service_notes || ''}
                  onChange={e => setEditing({ ...editing, last_service_notes: e.target.value })}
                  style={{ width: '100%' }} />
              </label>
              <label className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                     title="Operators enter the closing meter reading each day; the day's hours = today's reading minus the previous one">
                <input type="checkbox" checked={editing.metered === true}
                  onChange={e => setEditing({ ...editing, metered: e.target.checked })} />
                Has an hour meter — Daily Ops asks for the closing reading
              </label>
              <label className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" checked={editing.active !== false}
                  onChange={e => setEditing({ ...editing, active: e.target.checked })} />
                Active
              </label>
              <label className="small">Notes
                <textarea value={editing.notes || ''}
                  onChange={e => setEditing({ ...editing, notes: e.target.value })}
                  rows={2} style={{ width: '100%' }} />
              </label>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="refresh-btn" onClick={() => setEditing(null)} disabled={saving} style={{ background: '#888' }}>Cancel</button>
              <button className="refresh-btn" onClick={save} disabled={saving} style={{ background: '#0369a1' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
